import { useRef } from "react";

import {
    getBorrowPrincipalTransaction,
    BorrowPrincipalTxnParams,
    getDepositCollateralTransaction,
    ExpectedLoanValues,
    getWithdrawCollateralTransaction,
    RepayLoanTxnParams,
    getRefinanceLoanTransaction,
    RefinanceLoanTxnParams,
    getMergeLoanTransaction,
    MergeLoanTxnParams,
    convertLoanValueTupleToBps,
    DepositCollateralParams,
    SwapCollateralTxnParams,
    getSwapCollateralTransaction,
    getCreateLoanLutTransaction,
    LoanLutQuery,
    getRepayLoanTransactionAndBalanceChanges,
    buildLoanTransactions,
    BuildLoanTransactionsRequest,
    StakeLpQueryParams
} from "@bridgesplit/abf-sdk";
import { combineTransactionPromises } from "@bridgesplit/react";
import {
    decimalsToBps,
    Result,
    uiAmountToLamports,
    BalanceChanges,
    TransactionGenerationType,
    AnalyticsTransactionType,
    CollateralDepositAnalyticsInfo
} from "@bridgesplit/utils";

import { useUserTokens } from "../api";
import { useAbfFetches } from "../reducers";
import {
    AbfGeneratorResult,
    AbfTransactionDetails,
    SimulatedOrderedTransactions,
    TransactionSenderOptions,
    TransactionsWithBalanceChanges
} from "../types";
import {
    useAbfGenerateTransaction,
    useAbfGenerateTransactionWithData,
    bundleMultiTransactionBatches,
    wrapAsAbfGeneratorResult,
    wrapBundledAsAbfGeneratorResult
} from "./common";
import { useCreateSimulationPromise } from "./simulation";

export function useRepayLoanTransaction(isLoopUnwind = false): AbfTransactionDetails<RepayLoanTxnParams> & {
    getTransactionsWithParamsAndBalanceChanges: (
        params: RepayLoanTxnParams
    ) => Promise<Result<TransactionsWithBalanceChanges>>;
} {
    const { resetLoanApi, resetNapoleonApi, resetMarketsPublicApi, resetLoopApi, resetLendingVaultPositionsApi } =
        useAbfFetches();
    const { refresh } = useUserTokens();
    const generate = useAbfGenerateTransactionWithData();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    async function getTransactionsWithParamsAndBalanceChanges(
        params: RepayLoanTxnParams
    ): Promise<Result<TransactionsWithBalanceChanges>> {
        try {
            let balanceChanges: BalanceChanges | undefined;
            const repay = generate({
                generateFunction: getRepayLoanTransactionAndBalanceChanges,
                identifier: "Repay loan", // depending on what this is used for we might want to conditionally change it to Close Loop or smt
                params,
                analyticsInfo: params.analyticsInfo,
                onGenerate: (response) => {
                    balanceChanges = response.balanceChanges;
                }
            });

            const transactions = await combineTransactionPromises([repay], { order: "sequential" });
            if (transactions.isErr()) {
                return Result.err(transactions);
            }

            const nonEmptyBalanceChanges =
                balanceChanges && Object.keys(balanceChanges).length > 0 ? balanceChanges : undefined;

            const orderedTransactions = bundleMultiTransactionBatches(transactions.unwrap());
            return Result.ok({
                transactions: orderedTransactions,
                balanceChanges: nonEmptyBalanceChanges,
                simulationPromise: createSimulationPromise(orderedTransactions)
            });
        } catch (error) {
            return Result.err(error);
        }
    }

    async function getTransactionsWithParams(params: RepayLoanTxnParams): AbfGeneratorResult {
        const result = await getTransactionsWithParamsAndBalanceChanges(params);
        if (result.isErr()) return Result.err(result);
        const orderedTransactions = result.unwrap().transactions;
        return Result.ok({
            transactions: orderedTransactions,
            simulationPromise: createSimulationPromise(orderedTransactions, params.analyticsInfo)
        });
    }

    const closeLoopRefetch = () => {
        resetNapoleonApi();
        resetMarketsPublicApi();
        resetLoopApi();
        void refresh();
        resetLoanApi();
    };

    const repayLoanRefetch = () => {
        resetLoanApi();
        resetLendingVaultPositionsApi();
    };

    const sendOptions: TransactionSenderOptions = {
        allowBatchFailure: false,
        refetch: () => (isLoopUnwind ? closeLoopRefetch() : repayLoanRefetch())
    };

    return {
        getTransactionsWithParams,
        getTransactionsWithParamsAndBalanceChanges,
        sendOptions,
        description: "Repaying Loan"
    }; // todo maybe we call it closing position
}

export interface WithdrawCollateralTransactionParams {
    loan: string;
    collateralMint: string;
    amount: number;
    collateralIndex: number;
    assetIndexGuidance?: string; //Do not need to add derived in BE
    weightMatrix?: string; //Do not need to add derived in BE
    expectedLoanValues: ExpectedLoanValues;
    stakeInstructions?: StakeLpQueryParams;
}
export function useLoanWithdrawTransaction(): AbfTransactionDetails<WithdrawCollateralTransactionParams> {
    const { resetLoanApi, resetLendingVaultPositionsApi } = useAbfFetches();
    const { refresh } = useUserTokens();
    const generate = useAbfGenerateTransaction();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams(params: WithdrawCollateralTransactionParams): AbfGeneratorResult {
        return wrapAsAbfGeneratorResult(
            generate({ generateFunction: getWithdrawCollateralTransaction, identifier: "Withdraw assets", params }),
            createSimulationPromise,
            { order: "parallel" }
        );
    }

    const sendOptions = {
        refetch: () => {
            resetLoanApi();
            resetLendingVaultPositionsApi();
            void refresh();
        }
    };

    return { getTransactionsWithParams, sendOptions, description: "Closing Loan" };
}

export function useLoanRefinanceTransaction(): AbfTransactionDetails<RefinanceLoanTxnParams> {
    const { resetLoanApi } = useAbfFetches();
    const { refresh } = useUserTokens();
    const generate = useAbfGenerateTransaction();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams(params: RefinanceLoanTxnParams): AbfGeneratorResult {
        return wrapBundledAsAbfGeneratorResult(
            generate({ generateFunction: getRefinanceLoanTransaction, identifier: "Adjust terms", params }),
            createSimulationPromise
        );
    }

    const sendOptions = {
        refetch: () => {
            resetLoanApi();
            void refresh();
        }
    };

    return { getTransactionsWithParams, sendOptions, description: "Refinance loan" };
}

export function useMergeLoanTransaction(): AbfTransactionDetails<MergeLoanTxnParams> {
    const generate = useAbfGenerateTransaction();
    const { resetLoanApi, resetMarketsPublicApi, resetLoopApi } = useAbfFetches();
    const { refresh } = useUserTokens();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams(params: MergeLoanTxnParams): AbfGeneratorResult {
        return wrapBundledAsAbfGeneratorResult(
            generate({
                generateFunction: getMergeLoanTransaction,
                identifier: "Merge loans",
                params
            }),
            createSimulationPromise
        );
    }

    const sendOptions: TransactionSenderOptions = {
        refetch: () => {
            resetLoanApi();
            resetMarketsPublicApi();
            resetLoopApi();
            void refresh();
        }
    };

    return {
        getTransactionsWithParams,
        sendOptions,
        description: "Merge loans"
    };
}

/** One merge step per pair; multiple steps are combined with {@link combineTransactionPromises} `sequential` order. */
export interface MergeLoansSequentialParams {
    mergePairs: MergeLoanTxnParams[];
}

export function useMergeLoansSequentialTransaction(): AbfTransactionDetails<MergeLoansSequentialParams> {
    const generate = useAbfGenerateTransaction();
    const { resetLoanApi, resetMarketsPublicApi, resetLoopApi, resetUserApi } = useAbfFetches();
    const { refresh } = useUserTokens();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    async function getTransactionsWithParams({ mergePairs }: MergeLoansSequentialParams): AbfGeneratorResult {
        try {
            if (!mergePairs.length) {
                return Result.errFromMessage("No merge calls to execute");
            }

            const txnPromises = mergePairs.map((params, index) =>
                generate({
                    generateFunction: getMergeLoanTransaction,
                    identifier:
                        mergePairs.length > 1 ? `Merge loans (${index + 1}/${mergePairs.length})` : "Merge loans",
                    params
                })
            );

            const combined = await combineTransactionPromises(txnPromises, { order: "sequential" });
            if (combined.isErr()) {
                // `Result.err` de-nests an err `Result` (see result.ts) and re-types the phantom
                // success param, so this propagates the error without nesting.
                return Result.err(combined);
            }
            const ordered = bundleMultiTransactionBatches(combined.unwrap());
            return Result.ok({
                transactions: ordered,
                simulationPromise: createSimulationPromise(ordered)
            });
        } catch (error) {
            return Result.err(error);
        }
    }

    const sendOptions: TransactionSenderOptions = {
        refetch: () => {
            resetLoanApi();
            resetMarketsPublicApi();
            resetLoopApi();
            resetUserApi();
            void refresh();
        }
    };

    return {
        getTransactionsWithParams,
        sendOptions,
        description: "Merge loans"
    };
}

export interface IncreaseCreditTransactionParams extends BorrowPrincipalTxnParams {
    decimals: number;
}
export function useIncreasePrincipalTransaction(): AbfTransactionDetails<IncreaseCreditTransactionParams> {
    const generate = useAbfGenerateTransaction();
    const { resetLoanApi, resetNapoleonApi, resetMarketsPublicApi } = useAbfFetches();
    const { refresh } = useUserTokens();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams({
        borrowParams: { amount: increaseAmount, expectedLoanValues, duration },
        decimals,
        ...params
    }: IncreaseCreditTransactionParams): AbfGeneratorResult {
        return wrapAsAbfGeneratorResult(
            generate({
                generateFunction: getBorrowPrincipalTransaction,
                identifier: "Borrow more",
                params: {
                    ...params,
                    borrowParams: {
                        amount: uiAmountToLamports(increaseAmount, decimals),
                        duration,
                        expectedLoanValues: {
                            expectedApy: decimalsToBps(expectedLoanValues.expectedApy),
                            expectedLqt: convertLoanValueTupleToBps(expectedLoanValues.expectedLqt)
                        }
                    }
                }
            }),
            createSimulationPromise,
            { order: "parallel" }
        );
    }

    // Added because escrow patterns above
    const sendOptions = {
        refetch: () => {
            resetLoanApi();
            resetNapoleonApi();
            resetMarketsPublicApi();
            void refresh();
        }
    };

    return { getTransactionsWithParams, sendOptions, description: "Increasing borrow" };
}

export function useCreateLoanLutTransaction(): AbfTransactionDetails<LoanLutQuery> {
    const generate = useAbfGenerateTransaction();
    const { resetLoanApi, resetClmmPositions } = useAbfFetches();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams({ loan, actionType }: LoanLutQuery): AbfGeneratorResult {
        return wrapAsAbfGeneratorResult(
            generate({
                generateFunction: getCreateLoanLutTransaction,
                identifier: "Enable position management",
                params: { loan, actionType }
            }),
            createSimulationPromise,
            { order: "parallel" }
        );
    }

    const sendOptions = {
        refetch: () => {
            resetLoanApi();
            resetClmmPositions();
        }
    };

    return { getTransactionsWithParams, sendOptions, description: "Enable position management" };
}

export function useDepositCollateralTransaction(): AbfTransactionDetails<DepositCollateralParams> {
    const generate = useAbfGenerateTransaction();
    const { resetLoanApi, resetLoopApi, resetLendingVaultPositionsApi } = useAbfFetches();
    const { refresh } = useUserTokens();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams(params: DepositCollateralParams): AbfGeneratorResult {
        const amount = uiAmountToLamports(params.amount, params.decimals);
        const analyticsInfo: CollateralDepositAnalyticsInfo = {
            transactionType: AnalyticsTransactionType.CollateralDeposit,
            loanAddress: params.loan,
            depositMint: params.depositMint,
            amount,
            assetType: params.assetType,
            assetIdentifier: params.assetIdentifier,
            hasUnstakeStep: !!params.unstakeInstructions
        };
        return wrapAsAbfGeneratorResult(
            generate({
                generateFunction: getDepositCollateralTransaction,
                identifier: "Deposit Asset",
                params: {
                    ...params,
                    amount,
                    expectedLoanValues: {
                        expectedApy: decimalsToBps(params.expectedLoanValues.expectedApy),
                        expectedLqt: convertLoanValueTupleToBps(params.expectedLoanValues.expectedLqt)
                    }
                },
                analyticsInfo
            }),
            createSimulationPromise,
            { order: "parallel" },
            analyticsInfo
        );
    }

    const sendOptions = {
        refetch: () => {
            resetLoanApi();
            resetLoopApi();
            resetLendingVaultPositionsApi();
            void refresh();
        }
    };

    return { getTransactionsWithParams, sendOptions, description: "Deposit Assets" };
}

// Jito by default: every caller forwards pandora's ATA creates as a setup tx (unifySetup: false),
// and in Jito mode the BE keeps the tip on that setup tx and builds the main tx without any
// ComputeBudget ixs, which is the packet headroom a two-venue rollover needs.
export function useSwapCollateralTransaction(useJito = true): AbfTransactionDetails<SwapCollateralTxnParams> {
    const generate = useAbfGenerateTransaction();
    const { resetLoanApi, resetLoopApi } = useAbfFetches();
    const { refresh } = useUserTokens();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    function getTransactionsWithParams(params: SwapCollateralTxnParams): AbfGeneratorResult {
        return wrapBundledAsAbfGeneratorResult(
            generate({
                generateFunction: getSwapCollateralTransaction,
                identifier: "Swap Collateral",
                params,
                txnSettingsOverride: useJito ? { broadcastMode: TransactionGenerationType.Jito } : undefined
            }),
            createSimulationPromise
        );
    }

    const sendOptions = {
        refetch: () => {
            resetLoanApi();
            resetLoopApi();
            void refresh();
        }
    };

    return { getTransactionsWithParams, sendOptions, description: "Swap Collateral" };
}

export function useLoanBuildTransactions(useJito?: boolean): AbfTransactionDetails<BuildLoanTransactionsRequest> & {
    getLastLoanAddress: () => string | undefined;
} {
    const generate = useAbfGenerateTransactionWithData();
    const { resetNapoleonApi, resetMarketsPublicApi, resetLoopApi, resetLoanApi } = useAbfFetches();
    const { refresh } = useUserTokens();
    const lastLoanAddressRef = useRef<string>();
    const createSimulationPromise = useCreateSimulationPromise({ skipSimulate: true });

    async function getTransactionsWithParams(params: BuildLoanTransactionsRequest): AbfGeneratorResult {
        try {
            const builtTransactions = generate({
                generateFunction: buildLoanTransactions,
                identifier: "Build loan transactions",
                params,
                txnSettingsOverride: useJito ? { broadcastMode: TransactionGenerationType.Jito } : undefined,
                onGenerate: (response) => {
                    lastLoanAddressRef.current = response.loanAddress;
                }
            });

            const transactions = await combineTransactionPromises([builtTransactions], { order: "parallel" });
            if (transactions.isErr()) {
                const transactionsError = transactions.unwrap();
                return Result.err(transactionsError);
            }

            const orderedTransactions = bundleMultiTransactionBatches(transactions.unwrap());
            return Result.ok({
                transactions: orderedTransactions,
                simulationPromise: createSimulationPromise(orderedTransactions)
            });
        } catch (error) {
            return Result.err(error);
        }
    }

    const sendOptions: TransactionSenderOptions = {
        refetch: () => {
            resetNapoleonApi();
            resetMarketsPublicApi();
            resetLoopApi();
            resetLoanApi();
            void refresh();
        }
    };

    return {
        getTransactionsWithParams,
        sendOptions,
        description: "Build loan transactions",
        getLastLoanAddress: () => lastLoanAddressRef.current
    };
}

export function useBuildLoanTransactions(useJito?: boolean): AbfTransactionDetails<BuildLoanTransactionsRequest> & {
    getLastLoanAddress: () => string | undefined;
    getTransactionsWithParamsAndSimulation: (
        params: BuildLoanTransactionsRequest
    ) => Promise<Result<SimulatedOrderedTransactions>>;
} {
    const base = useLoanBuildTransactions(useJito);
    const createSimulationPromise = useCreateSimulationPromise({
        skipSimulate: !useJito,
        captureBalanceChanges: true
    });

    async function getTransactionsWithParams(params: BuildLoanTransactionsRequest): AbfGeneratorResult {
        const result = await base.getTransactionsWithParams(params);
        if (result.isErr()) return result;
        const { transactions } = result.unwrap();
        return Result.ok({
            transactions,
            simulationPromise: createSimulationPromise(transactions)
        });
    }

    // Blocking simulation for callers that need to await a pass/fail before proceeding.
    // Uses simulationPromise internally so all simulation routes through simulateOrderedTransactions.
    async function getTransactionsWithParamsAndSimulation(
        params: BuildLoanTransactionsRequest
    ): Promise<Result<SimulatedOrderedTransactions>> {
        const result = await getTransactionsWithParams(params);
        if (result.isErr()) return Result.err(result);

        const { transactions, simulationPromise } = result.unwrap();
        const simResult = await simulationPromise;
        if (simResult?.error) return Result.errFromMessage(simResult.error, { skipSentry: true });

        return Result.ok({
            transactions,
            balanceChanges: simResult?.balanceChanges
        });
    }

    return {
        ...base,
        getTransactionsWithParams,
        getTransactionsWithParamsAndSimulation
    };
}
